import * as THREE from 'three';
import type { BakedFrame } from '../engine/bake.ts';
import { adoptGlow } from '../materials.ts';

/**
 * A nightmare's boil (its four stop-motion frames) in one draw: the frames share materials and vertex counts
 * and differ only in where each vertex sits and which way it faces, so every frame's positions and normals go
 * into one atlas texture and each vertex carries where its own run starts (`aBoil`). An instance says which
 * frame it shows in `aAnimB.w` (bones + frame × 256), and the shader reads that frame's vertex instead of the
 * attribute. Same vertices, same picture; a quarter of the draw calls.
 */
const W = 2048;
let data = new Float32Array(W * 64 * 4);
let used = 0;

const makeTex = (): THREE.DataTexture => {
  const t = new THREE.DataTexture(data, W, data.length / 4 / W, THREE.RGBAFormat, THREE.FloatType);
  t.needsUpdate = true;
  return t;
};
export const boilTexture = { value: makeTex() };

/** At most this many frames ride in `aAnimB.w` next to the bone count (under 256). */
export const BOIL_MAX_FRAMES = 32;

const RUNS = new WeakMap<THREE.BufferGeometry, THREE.BufferAttribute>();

/** Part `part` of every frame into the atlas (once per baked part): the per-vertex start of its run. */
export const boilRuns = (frames: readonly BakedFrame[], part: number): THREE.BufferAttribute => {
  const g = (frames[0] as BakedFrame).parts[part]?.geometry as THREE.BufferGeometry;
  // A far version (engine/lod.ts) draws the same vertices: the same run.
  const g0 = (g.userData.full as THREE.BufferGeometry | undefined) ?? g;
  const done = RUNS.get(g0);
  if (done) return done;
  const n = g0.getAttribute('position').count;
  const F = frames.length;
  const need = n * F * 2;
  if ((used + need) * 4 > data.length) {
    let rows = data.length / 4 / W;
    while ((used + need) * 4 > rows * W * 4) rows *= 2;
    const next = new Float32Array(rows * W * 4);
    next.set(data);
    data = next;
    const old = boilTexture.value;
    boilTexture.value = makeTex();
    old.dispose();
  }
  const runs = new Float32Array(n);
  for (let f = 0; f < F; f++) {
    const g = (frames[f] as BakedFrame).parts[part]?.geometry as THREE.BufferGeometry;
    const pos = g.getAttribute('position');
    const nrm = g.getAttribute('normal');
    for (let v = 0; v < n; v++) {
      const at = (used + v * F * 2 + f * 2) * 4;
      data[at] = pos.getX(v);
      data[at + 1] = pos.getY(v);
      data[at + 2] = pos.getZ(v);
      data[at + 4] = nrm.getX(v);
      data[at + 5] = nrm.getY(v);
      data[at + 6] = nrm.getZ(v);
    }
  }
  for (let v = 0; v < n; v++) runs[v] = used + v * F * 2;
  used += need;
  boilTexture.value.needsUpdate = true;
  const attr = new THREE.BufferAttribute(runs, 1);
  RUNS.set(g0, attr);
  return attr;
};

/** `?boil=0`: every boil frame in its own draw as before, to compare side by side. */
const OFF = typeof location !== 'undefined' && new URLSearchParams(location.search).get('boil') === '0';

/** Whether a rigged model's frames can boil in one draw: same materials and vertex data but for positions and normals. */
export const canBoil = (frames: readonly BakedFrame[]): boolean => {
  const f0 = frames[0];
  if (OFF || !f0 || frames.length < 2 || frames.length > BOIL_MAX_FRAMES) return false;
  for (const f of frames) {
    if (f.parts.length !== f0.parts.length) return false;
    for (let i = 0; i < f.parts.length; i++) {
      const a = f0.parts[i] as BakedFrame['parts'][number];
      const b = f.parts[i] as BakedFrame['parts'][number];
      if (a.material !== b.material) return false;
      for (const [k, att] of Object.entries(a.geometry.attributes)) {
        const other = b.geometry.getAttribute(k);
        if (!other || other.count !== att.count || other.itemSize !== att.itemSize) return false;
        if (k === 'position' || k === 'normal' || f === f0) continue;
        const x = att.array;
        const y = other.array;
        for (let j = 0; j < x.length; j++) if (x[j] !== y[j]) return false;
      }
    }
  }
  return true;
};

const DECL = /* glsl */ `
attribute float aBoil;
uniform highp sampler2D uBoil;
vec4 boilTexel(float i) {
  int k = int(i + 0.5);
  int w = textureSize(uBoil, 0).x;
  return texelFetch(uBoil, ivec2(k - (k / w) * w, k / w), 0);
}
`;

const VARIANTS = new WeakMap<THREE.Material, THREE.Material>();

/** The boiling twin of a skinned material (anim/skin.ts): each vertex read from the instance's frame in the atlas. */
export const boiled = (s: THREE.Material): THREE.Material => {
  let v = VARIANTS.get(s);
  if (v) return v;
  const c = s.clone();
  const prev = s.onBeforeCompile;
  const prevKey = s.customProgramCacheKey();
  c.onBeforeCompile = (shader, renderer): void => {
    prev.call(s, shader, renderer);
    shader.uniforms.uBoil = boilTexture;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${DECL}`)
      .replace('float bones = aAnimB.w;', 'float bones = mod(aAnimB.w, 256.0);')
      .replace('objectNormal = animRot(objectNormal);', 'objectNormal = animRot(boilTexel(aBoil + floor(aAnimB.w / 256.0) * 2.0 + 1.0).xyz);')
      .replace('transformed = animRot(transformed * animT.w)', 'transformed = animRot(boilTexel(aBoil + floor(aAnimB.w / 256.0) * 2.0).xyz * animT.w)');
  };
  c.customProgramCacheKey = (): string => `${prevKey}|somnhold-boil`;
  c.name = `${s.name}+boil`;
  c.forceSinglePass = s.forceSinglePass;
  adoptGlow(c);
  v = c;
  VARIANTS.set(s, v);
  return v;
};
